import React, { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Table, Button, Space, Empty, message } from "antd";
import { PageHeader } from "../../../ui";
import { useAuth } from "../../../hooks/useAuth";
import { dashboardApi } from "../../../api/dashboardAPI";
import dayjs from "dayjs";

function MiniTotals({ data, loading }) {
  const count = data?.count ?? 0;
  const sums = data?.items?.reduce(
    (acc, it) => {
      acc.instituciones += it?.totales?.instituciones ?? 0;
      acc.profesores += it?.totales?.profesores ?? 0;
      acc.alumnos += it?.totales?.alumnos ?? 0;
      return acc;
    },
    { instituciones: 0, profesores: 0, alumnos: 0 },
  ) || { instituciones: 0, profesores: 0, alumnos: 0 };

  const cards = [
    { title: "Partners cargados", value: count },
    { title: "Instituciones (suma por partner)", value: sums.instituciones },
    { title: "Profesores (suma por partner)", value: sums.profesores },
    { title: "Alumnos (suma por partner)", value: sums.alumnos },
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
      {cards.map((c) => (
        <Col key={c.title} xs={24} sm={12} md={12} lg={6}>
          <Card
            loading={loading}
            bordered
            hoverable
            style={{ borderRadius: 10 }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>{c.title}</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              en los partners listados
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

export function Page_DashboardPartners() {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await dashboardApi.getPartnersList(accessToken);
        if (alive) setPayload(data);
      } catch (e) {
        const msg = e?.body?.message || e?.message || "Error cargando partners";
        if (alive) {
          setError(msg);
          message.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken]);

  const columns = useMemo(
    () => [
      {
        title: "Partner",
        dataIndex: "nombre",
        sorter: (a, b) => (a?.nombre || "").localeCompare(b?.nombre || ""),
        render: (v, row) => (
          <div>
            <div style={{ fontWeight: 600 }}>{v || row.email || "—"}</div>
            <div style={{ color: "#6b7280", fontSize: 12 }}>
              {row?.direccion || "—"}
            </div>
          </div>
        ),
      },
      {
        title: "Fecha alta",
        dataIndex: "fechaAltaPartner",
        sorter: (a, b) =>
          new Date(a?.fechaAltaPartner || 0) -
          new Date(b?.fechaAltaPartner || 0),
        render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
        width: 140,
      },
      {
        title: "Instituciones",
        dataIndex: ["totales", "instituciones"],
        align: "right",
        width: 130,
      },
      {
        title: "Profesores",
        dataIndex: ["totales", "profesores"],
        align: "right",
        width: 120,
      },
      {
        title: "Alumnos",
        dataIndex: ["totales", "alumnos"],
        align: "right",
        width: 110,
      },
      {
        title: "Acciones",
        key: "actions",
        width: 160,
        render: (_, row) => (
          <Space>
            <Button
              type="default"
              size="small"
              onClick={() => {}}
              aria-label={`Mostrar/ocultar instituciones de ${row?.nombre || "partner"}`}
            >
              Instituciones
            </Button>
          </Space>
        ),
      },
    ],
    [],
  );

  const expandedRowRender = (partner) => {
    const items = partner?.instituciones || [];
    if (!items.length) {
      return <Empty description="Este partner no tiene instituciones aún" />;
    }

    const cols = [
      { title: "Institución", dataIndex: "nombre", key: "nombre" },
      {
        title: "Fecha alta",
        dataIndex: "fechaAlta",
        key: "fechaAlta",
        width: 140,
        render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
      },
      {
        title: "Profesores",
        dataIndex: "profesores",
        align: "right",
        width: 110,
      },
      { title: "Alumnos", dataIndex: "alumnos", align: "right", width: 110 },
      {
        title: "Acción",
        key: "ver",
        width: 100,
        render: (_, row) => (
          <Button
            type="link"
            size="small"
            href={`/#/admin/dashboard/institutions/${row._id}`}
            aria-label={`Ver institución ${row?.nombre || ""}`}
          >
            Ver
          </Button>
        ),
      },
    ];
    return (
      <Table
        size="small"
        rowKey={(r) => r._id}
        columns={cols}
        dataSource={items}
        pagination={false}
      />
    );
  };

  return (
    <div className="page-dashboard">
      <PageHeader
        title="Partners"
        subtitle="Listado de partners con instituciones anidadas"
      />

      <MiniTotals data={payload} loading={loading} />

      <Table
        rowKey={(r) => r._id}
        loading={loading}
        dataSource={payload?.items || []}
        columns={columns}
        expandable={{ expandedRowRender }}
        locale={{
          emptyText: error ? "No se pudo cargar la información" : "Sin datos",
        }}
      />
    </div>
  );
}
